import type { AuditPort } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/**
 * Journal d'audit adossé à `audit_logs`.
 *
 * Aucune session n'écrit dans la table : la policy `audit_no_user_writes`
 * refuse toute insertion. On passe par `record_audit_entry`
 * (`20260301005900`), qui prend l'entreprise et l'auteur de la session —
 * jamais de l'appel — et n'accepte que ce qu'une session peut déclarer. Le
 * `tenantId` et l'`actorId` du contexte ne sont donc pas transmis : la base
 * les connaît mieux que nous.
 *
 * Un échec d'écriture ne fait pas échouer l'action métier — perdre une ligne
 * de journal est ennuyeux, refuser un devis au patron parce que le journal
 * était indisponible l'est davantage. En revanche il est signalé, sans quoi
 * un audit silencieusement vide passerait pour un audit propre.
 */
export function createSupabaseAudit(
  clientFor: DbClientFactory,
  onFailure?: (error: unknown) => void,
): AuditPort {
  return {
    async record(entry) {
      const { ctx } = entry;
      const { error } = await clientFor(ctx).rpc("record_audit_entry", {
        p_action: entry.action,
        p_actor_type: ctx.actorType,
        p_channel: ctx.channel,
        p_request_id: ctx.requestId,
        p_payload_hash: entry.payloadHash ?? null,
        p_result: entry.result,
        p_details: entry.details ?? null,
      });

      if (error) onFailure?.(error);
    },
  };
}
